/**
 * Audio engine — SFX + ambient BGM playback with low default volumes.
 *
 * Design goals:
 * - Default volumes are intentionally low (BGM 0.5, SFX 0.55) so ambience
 *   never becomes distracting. Tracks are also rendered quietly so even at
 *   default volume they sit underneath dialogue.
 * - Cross-fades smoothly between BGM tracks (0.8s).
 * - Tolerates missing audio files / browsers without WebAudio: every public
 *   method becomes a no-op rather than throwing.
 * - All settings persist to localStorage and reload across sessions.
 */

const STORAGE_KEY = 'kan-vn-audio-settings';
const DEFAULT_SFX_VOLUME = 0.55;
const DEFAULT_BGM_VOLUME = 0.5;
const BGM_CROSSFADE_SECONDS = 0.8;

export interface AudioSettings {
  sfxVolume: number;
  sfxMuted: boolean;
  bgmVolume: number;
  bgmMuted: boolean;
  /**
   * Master mute — when true, both SFX and BGM buses are silenced regardless
   * of their individual mute/volume state. Toggled by the persistent
   * top-right mute button.
   */
  masterMuted: boolean;
}

export type AudioSettingsListener = (settings: Readonly<AudioSettings>) => void;

function loadSettings(): AudioSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isPartialSettings(parsed)) {
        return mergeWithDefaults(parsed);
      }
    }
  } catch {
    // ignore
  }
  return defaults();
}

function defaults(): AudioSettings {
  return {
    sfxVolume: DEFAULT_SFX_VOLUME,
    sfxMuted: false,
    bgmVolume: DEFAULT_BGM_VOLUME,
    bgmMuted: false,
    masterMuted: false,
  };
}

function mergeWithDefaults(p: Partial<AudioSettings>): AudioSettings {
  const d = defaults();
  return {
    sfxVolume: typeof p.sfxVolume === 'number' ? clamp01(p.sfxVolume) : d.sfxVolume,
    sfxMuted: typeof p.sfxMuted === 'boolean' ? p.sfxMuted : d.sfxMuted,
    bgmVolume: typeof p.bgmVolume === 'number' ? clamp01(p.bgmVolume) : d.bgmVolume,
    bgmMuted: typeof p.bgmMuted === 'boolean' ? p.bgmMuted : d.bgmMuted,
    masterMuted: typeof p.masterMuted === 'boolean' ? p.masterMuted : d.masterMuted,
  };
}

function saveSettings(settings: AudioSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // quota exceeded — silently ignore
  }
}

function isPartialSettings(value: unknown): value is Partial<AudioSettings> {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  // Accept any object that has at least one known field.
  return (
    'sfxVolume' in obj ||
    'sfxMuted' in obj ||
    'bgmVolume' in obj ||
    'bgmMuted' in obj ||
    'masterMuted' in obj
  );
}

/** Clamp a number between 0 and 1. */
function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

interface BgmHandle {
  source: AudioBufferSourceNode;
  gain: GainNode;
  path: string;
}

/**
 * Singleton audio engine.
 * Call `AudioEngine.getInstance()` to get the shared instance.
 */
export class AudioEngine {
  private static instance: AudioEngine | null = null;

  private ctx: AudioContext | null = null;
  private settings: AudioSettings;

  /** Master gain node for the SFX bus. */
  private sfxBus: GainNode | null = null;
  /** Master gain node for the BGM bus. */
  private bgmBus: GainNode | null = null;

  /** Currently playing BGM. */
  private currentBgm: BgmHandle | null = null;

  /** Cache decoded audio buffers to avoid re-fetching. */
  private bufferCache = new Map<string, AudioBuffer>();

  /** Subscribers notified whenever audio settings change. */
  private listeners = new Set<AudioSettingsListener>();

  private constructor() {
    this.settings = loadSettings();
  }

  /**
   * Subscribe to settings changes (e.g. for UI mute-button sync).
   * Returns an unsubscribe function.
   */
  subscribe(listener: AudioSettingsListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    const snapshot = this.getSettings();
    for (const l of this.listeners) {
      try {
        l(snapshot);
      } catch (err) {
        console.warn('[AudioEngine] listener threw:', err);
      }
    }
  }

  static getInstance(): AudioEngine {
    if (!AudioEngine.instance) {
      AudioEngine.instance = new AudioEngine();
    }
    return AudioEngine.instance;
  }

  /** Reset the singleton (useful for tests). */
  static resetInstance(): void {
    if (AudioEngine.instance) {
      AudioEngine.instance.destroy();
      AudioEngine.instance = null;
    }
  }

  /** Ensure the AudioContext is created and resumed (must be called from a user gesture). */
  async ensureContext(): Promise<void> {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
      } catch {
        // AudioContext not available
        return;
      }
    }
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
    if (!this.sfxBus) {
      this.sfxBus = this.ctx.createGain();
      this.sfxBus.connect(this.ctx.destination);
      this.applySfxVolume();
    }
    if (!this.bgmBus) {
      this.bgmBus = this.ctx.createGain();
      this.bgmBus.connect(this.ctx.destination);
      this.applyBgmVolume();
    }
  }

  /** Play a one-shot sound effect. */
  async playSfx(path: string): Promise<void> {
    await this.ensureContext();
    if (!this.ctx || !this.sfxBus) return;
    if (this.settings.masterMuted) return;
    if (this.settings.sfxMuted || this.settings.sfxVolume === 0) return;

    const buffer = await this.loadBuffer(path);
    if (!buffer) return;

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.sfxBus);
    source.start(0);
  }

  /**
   * Play an ambient looping BGM track. If the same track is already playing,
   * this is a no-op. Otherwise cross-fades from the current track.
   */
  async playBgm(path: string): Promise<void> {
    await this.ensureContext();
    if (!this.ctx || !this.bgmBus) return;
    if (this.currentBgm?.path === path) return;

    const buffer = await this.loadBuffer(path);
    if (!buffer) {
      // Asset missing — keep playing existing track if any.
      return;
    }

    const now = this.ctx.currentTime;

    // Build new source.
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.connect(this.bgmBus);

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    source.start(0);

    // Fade in the new source.
    gain.gain.linearRampToValueAtTime(1, now + BGM_CROSSFADE_SECONDS);

    // Fade out and stop the old source.
    if (this.currentBgm) {
      const old = this.currentBgm;
      old.gain.gain.cancelScheduledValues(now);
      old.gain.gain.setValueAtTime(old.gain.gain.value, now);
      old.gain.gain.linearRampToValueAtTime(0, now + BGM_CROSSFADE_SECONDS);
      try {
        old.source.stop(now + BGM_CROSSFADE_SECONDS + 0.05);
      } catch {
        /* ignore */
      }
    }

    this.currentBgm = { source, gain, path };
  }

  /** Stop the current BGM (with fade out). */
  stopBgm(): void {
    if (!this.ctx || !this.currentBgm) return;
    const now = this.ctx.currentTime;
    const old = this.currentBgm;
    old.gain.gain.cancelScheduledValues(now);
    old.gain.gain.setValueAtTime(old.gain.gain.value, now);
    old.gain.gain.linearRampToValueAtTime(0, now + BGM_CROSSFADE_SECONDS);
    try {
      old.source.stop(now + BGM_CROSSFADE_SECONDS + 0.05);
    } catch {
      /* ignore */
    }
    this.currentBgm = null;
  }

  /** The file path of the currently playing BGM track, or null if nothing is playing. */
  get currentBgmPath(): string | null {
    return this.currentBgm?.path ?? null;
  }

  get sfxVolume(): number {
    return this.settings.sfxVolume;
  }

  set sfxVolume(v: number) {
    this.settings.sfxVolume = clamp01(v);
    this.applySfxVolume();
    this.persist();
    this.emit();
  }

  get sfxMuted(): boolean {
    return this.settings.sfxMuted;
  }

  set sfxMuted(v: boolean) {
    this.settings.sfxMuted = v;
    this.applySfxVolume();
    this.persist();
    this.emit();
  }

  get bgmVolume(): number {
    return this.settings.bgmVolume;
  }

  set bgmVolume(v: number) {
    this.settings.bgmVolume = clamp01(v);
    this.applyBgmVolume();
    this.persist();
    this.emit();
  }

  get bgmMuted(): boolean {
    return this.settings.bgmMuted;
  }

  set bgmMuted(v: boolean) {
    this.settings.bgmMuted = v;
    this.applyBgmVolume();
    this.persist();
    this.emit();
  }

  /** Master mute — silences all audio (BGM + SFX) when true. */
  get masterMuted(): boolean {
    return this.settings.masterMuted;
  }

  set masterMuted(v: boolean) {
    if (this.settings.masterMuted === v) return;
    this.settings.masterMuted = v;
    this.applySfxVolume();
    this.applyBgmVolume();
    this.persist();
    this.emit();
  }

  /** Toggle master mute and return the new state. */
  toggleMasterMute(): boolean {
    this.masterMuted = !this.settings.masterMuted;
    return this.settings.masterMuted;
  }

  /** Get a snapshot of current settings (for UI display). */
  getSettings(): Readonly<AudioSettings> {
    return { ...this.settings };
  }

  // ── Internal ──

  private applySfxVolume(): void {
    if (this.sfxBus && this.ctx) {
      const vol =
        this.settings.masterMuted || this.settings.sfxMuted
          ? 0
          : this.settings.sfxVolume;
      this.sfxBus.gain.setValueAtTime(vol, this.ctx.currentTime);
    }
  }

  private applyBgmVolume(): void {
    if (this.bgmBus && this.ctx) {
      const vol =
        this.settings.masterMuted || this.settings.bgmMuted
          ? 0
          : this.settings.bgmVolume;
      this.bgmBus.gain.setValueAtTime(vol, this.ctx.currentTime);
    }
  }

  private async loadBuffer(path: string): Promise<AudioBuffer | null> {
    if (this.bufferCache.has(path)) {
      return this.bufferCache.get(path)!;
    }
    if (!this.ctx) return null;

    try {
      const response = await fetch(path);
      if (!response.ok) {
        console.warn(`[AudioEngine] Failed to fetch: ${path} (${response.status})`);
        return null;
      }
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.ctx.decodeAudioData(arrayBuffer);
      this.bufferCache.set(path, audioBuffer);
      return audioBuffer;
    } catch (err) {
      console.warn(`[AudioEngine] Failed to load audio: ${path}`, err);
      return null;
    }
  }

  private persist(): void {
    saveSettings(this.settings);
  }

  /** Tear down the audio engine. */
  destroy(): void {
    if (this.currentBgm) {
      try {
        this.currentBgm.source.stop();
      } catch {
        /* ignore */
      }
      this.currentBgm = null;
    }
    if (this.ctx) {
      void this.ctx.close().catch(() => {
        /* ignore */
      });
      this.ctx = null;
    }
    this.sfxBus = null;
    this.bgmBus = null;
    this.bufferCache.clear();
  }
}
